<template>
  <div class="summary-assignment" data-testid="task-summary-assignment">
    <div class="summary-assignment__header">Assignment</div>
    <div class="summary-assignment__body">
      <div class="summary-assignment__section px-4 py-2" data-testid="task-summary-assignment__location">
        <p class="summary-assignment__section-label">Location</p>
        <div class="d-flex align-center">
          <img src="/img/icons/fbc-icons/LocationIconBlack.svg" alt="location" class="mr-1">
          <span class="summary-assignment__value">{{ locationName }}</span>
        </div>
      </div>

      <div class="summary-assignment__section px-4 py-2 border-top" data-testid="task-summary-assignment__preparer">
        <p class="summary-assignment__section-label">Preparer</p>
        <div class="d-flex flex-wrap summary-assignment__users">
          <div
            v-for="user in preparerUsers"
            :key="user.id"
            class="summary-assignment__user d-flex align-center pa-1"
          >
            <div class="summary-assignment__avatar-wrap">
              <v-avatar
                size="24"
                class="summary-assignment__avatar white--text"
                :class="getPreparerAvatarClass(user.id)"
              >
                <span class="summary-assignment__avatar-text">{{ user.initial }}</span>
              </v-avatar>
              <span
                v-if="showPreparerCompleteBadge(user.id)"
                class="summary-assignment__badge"
                aria-hidden="true"
              >
                <img
                  src="/img/icons/fbc-icons/ApproverCompleteBadge.svg"
                  alt=""
                  class="summary-assignment__badge-image"
                />
              </span>
            </div>
            <span class="summary-assignment__user-name">{{ user.name }}</span>
          </div>
          <span v-if="!preparerUsers.length" class="summary-assignment__value">—</span>
        </div>
      </div>

      <div
        class="summary-assignment__section px-4 py-3"
        :class="{ 'summary-assignment__section--with-sign-off': showSignOffSection }"
        data-testid="task-summary-assignment__approver"
      >
        <div class="summary-assignment__section-heading">
          <p class="summary-assignment__section-label">Approver</p>
          <v-tooltip
            v-if="showAutoApprovalRejectedBadge"
            top
            open-delay="200"
            content-class="tooltip-top summary-assignment__auto-rejected-tooltip"
          >
            <template #activator="{ on, attrs }">
              <span
                class="summary-assignment__auto-rejected-badge"
                data-testid="task-summary-assignment__auto-rejected-badge"
                v-bind="attrs"
                v-on="on"
              >
                <img
                  src="/img/icons/fbc-icons/AutoApprovalRejectedAlert.svg"
                  alt=""
                  class="summary-assignment__auto-rejected-icon"
                />
                {{ autoApprovalRejectedBadgeLabel }}
              </span>
            </template>
            <div class="summary-assignment__auto-rejected-tooltip-content">
              <p class="summary-assignment__auto-rejected-tooltip-title">
                {{ autoApprovalRejectedBadgeLabel }}
              </p>
              <p class="summary-assignment__auto-rejected-tooltip-body">
                {{ autoApprovalRejectedTooltipBody }}
              </p>
            </div>
          </v-tooltip>
          <span
            v-else-if="showAutoApproveEnabledBadge"
            class="summary-assignment__auto-approve-enabled-chip"
            data-testid="task-summary-assignment__auto-approve-enabled-badge"
          >
            <StatusIndicator
              :variant="statusVariants.DISABLED"
              :custom-label="autoApproveEnabledBadgeLabel"
              size="sm"
              outlined
            />
          </span>
        </div>
        <div class="summary-assignment__approver-row">
          <div class="summary-assignment__users summary-assignment__users--approver">
            <div
              v-if="showAutoApprovedActor"
              class="summary-assignment__user summary-assignment__user--system d-flex align-center pa-1"
              data-testid="task-summary-assignment__auto-approved-actor"
            >
              <div class="summary-assignment__avatar-wrap">
                <v-avatar
                  size="24"
                  class="summary-assignment__avatar summary-assignment__avatar--completed white--text"
                  data-testid="task-summary-assignment__auto-approved-actor-avatar"
                >
                  <img
                    src="/img/icons/fbc-icons/SystemActorGlyph.svg"
                    alt=""
                    class="summary-assignment__system-glyph"
                  />
                </v-avatar>
                <span class="summary-assignment__badge" aria-hidden="true">
                  <img
                    src="/img/icons/fbc-icons/ApproverCompleteBadge.svg"
                    alt=""
                    class="summary-assignment__badge-image"
                  />
                </span>
              </div>
              <span class="summary-assignment__system-actor-label">{{ autoApprovedActorLabel }}</span>
            </div>
            <div
              v-for="user in l1Users"
              :key="user.id"
              class="summary-assignment__user d-flex align-center pa-1"
              :data-testid="`task-summary-assignment__approver--${user.id}`"
            >
              <div class="summary-assignment__avatar-wrap">
                <v-avatar
                  size="24"
                  class="summary-assignment__avatar white--text"
                  :class="getL1AvatarClass(user.id)"
                >
                  <span class="summary-assignment__avatar-text">{{ user.initial }}</span>
                </v-avatar>
                <span
                  v-if="showL1CompleteBadge(user.id)"
                  class="summary-assignment__badge"
                  aria-hidden="true"
                >
                  <img
                    src="/img/icons/fbc-icons/ApproverCompleteBadge.svg"
                    alt=""
                    class="summary-assignment__badge-image"
                  />
                </span>
              </div>
              <span class="summary-assignment__user-name">{{ user.name }}</span>
            </div>
            <span v-if="!l1Users.length && !showAutoApprovedActor" class="summary-assignment__value">—</span>
          </div>
        </div>

        <div
          v-if="showSignOffSection"
          class="summary-assignment__sign-off"
          data-testid="task-summary-assignment__sign-off"
        >
          <div class="summary-assignment__sign-off-inner">
            <div class="summary-assignment__sign-off-header d-flex align-center">
              <span class="summary-assignment__sign-off-label">{{ signOffLabels.LABEL }}</span>
              <span
                v-if="showSignOffAnyOneOfChip"
                class="summary-assignment__sign-off-chip"
                data-testid="task-summary-assignment__sign-off-chip"
              >
                {{ signOffLabels.ANY_ONE_OF_LABEL }}
              </span>
            </div>
            <div class="d-flex flex-wrap summary-assignment__users summary-assignment__users--sign-off">
              <div
                v-for="user in l2Users"
                :key="user.id"
                class="summary-assignment__user d-flex align-center pa-1"
                :data-testid="`task-summary-assignment__sign-off-approver--${user.id}`"
              >
                <div class="summary-assignment__avatar-wrap">
                  <v-avatar
                    size="24"
                    class="summary-assignment__avatar white--text"
                    :class="getL2AvatarClass(user.id)"
                  >
                    <span class="summary-assignment__avatar-text">{{ user.initial }}</span>
                  </v-avatar>
                  <span
                    v-if="showL2CompleteBadge(user.id)"
                    class="summary-assignment__badge"
                    aria-hidden="true"
                  >
                    <img
                      src="/img/icons/fbc-icons/ApproverCompleteBadge.svg"
                      alt=""
                      class="summary-assignment__badge-image"
                    />
                  </span>
                </div>
                <span class="summary-assignment__user-name">{{ user.name }}</span>
              </div>
              <span v-if="!l2Users.length" class="summary-assignment__value">—</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './TaskSummaryAssignment.scss';
import StatusIndicator from '@/components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '@/components/utils/status-constants';
import { SIGN_OFF, AUTO_APPROVE, USER_ROLE, TASK_STAGES } from '@/modules/financialclose/utils/constants';
import { isAutoApprovedResubmission } from '@/modules/financialclose/utils/tasklistHelpers';
import { buildTaskApproverCellModel } from '@/modules/financialclose/views/Project/TaskList/utils/taskApproverCellUtils';
import { activeUserIdsFromUserRoles, normalizeUserIdList } from '@/modules/financialclose/views/Setup/TaskList/utils/assignmentRoles';

export default {
  name: 'TaskSummaryAssignment',

  components: {
    StatusIndicator,
  },

  props: {
    task: { type: Object, required: true },
  },

  computed: {
    signOffLabels() {
      return SIGN_OFF;
    },
    statusVariants() {
      return STATUS_VARIANTS;
    },
    autoApproveEnabledBadgeLabel() {
      return AUTO_APPROVE.ENABLED_BADGE;
    },
    autoApprovalRejectedBadgeLabel() {
      return AUTO_APPROVE.REJECTED_BADGE;
    },
    autoApprovalRejectedTooltipBody() {
      return AUTO_APPROVE.REJECTED_TOOLTIP_BODY;
    },
    isRejectedAfterAutoApproval() {
      return isAutoApprovedResubmission(this.task);
    },
    /** Stays true for the cycle after reject or return to pending, including after a later manual approval. */
    isAutoApproveSuppressed() {
      return this.task?.approval_state?.auto_approve_suppressed === true
        || this.task?.auto_approve_suppressed === true;
    },
    showAutoApprovalRejectedBadge() {
      return this.task?.auto_approve === true && this.isAutoApproveSuppressed;
    },
    showAutoApproveEnabledBadge() {
      return this.task?.auto_approve === true && !this.isAutoApproveSuppressed;
    },
    showAutoApprovedActor() {
      return !!this.approverCellModel.l1AutoApproved && !this.isRejectedAfterAutoApproval;
    },
    autoApprovedActorLabel() {
      return AUTO_APPROVE.DETAIL_BADGE;
    },
    userMap() {
      return this.$store.getters['tasks/getUserMap'] || {};
    },
    locationMap() {
      return this.$store.getters['tasks/getLocationMap'] || {};
    },
    locationName() {
      const loc = this.locationMap[this.task.location];
      return loc?.name || this.task.locationName || '—';
    },
    preparerUsers() {
      const ids =
        Array.isArray(this.task?.user_roles) && this.task.user_roles.length
          ? activeUserIdsFromUserRoles(this.task.user_roles, USER_ROLE.PREPARER)
          : normalizeUserIdList(this.task.preparer);
      return this.resolveUsers(ids);
    },
    approverCellModel() {
      return buildTaskApproverCellModel(this.task, this.userMap);
    },
    l1Users() {
      return this.approverCellModel.l1?.users || [];
    },
    l2Users() {
      return this.approverCellModel.l2?.users || [];
    },
    showSignOffSection() {
      return !!this.approverCellModel.requiresSignOff;
    },
    showSignOffAnyOneOfChip() {
      return this.l2Users.length >= 2;
    },
  },

  methods: {
    resolveUsers(ids) {
      const list = Array.isArray(ids) ? ids : (ids ? [ids] : []);
      return list.map((id) => {
        const user = this.userMap[id] || {};
        const name = user.name || id;
        return {
          id,
          name,
          initial: (name[0] || '?').toUpperCase(),
        };
      }).filter((u) => u.name);
    },

    isGroupUserCompleted(userId, group) {
      if (!group?.completed) return false;
      if (group.completedBy?.id) {
        return String(group.completedBy.id) === String(userId);
      }
      if ((group.users || []).length === 1) {
        return String(group.users[0].id) === String(userId);
      }
      return false;
    },

    isAwaitingSubmission() {
      const stage = Number(this.task?.stage);
      return stage === TASK_STAGES.PENDING_SUBMISSION || stage === -1;
    },

    submitterId() {
      const roles = Array.isArray(this.task?.user_roles) ? this.task.user_roles : [];
      let matchId = null;
      let matchAt = -1;
      roles.forEach((roleEntry) => {
        if (roleEntry?.status === false || roleEntry?.role !== USER_ROLE.PREPARER) return;
        const actions = Array.isArray(roleEntry.actions_performed) ? roleEntry.actions_performed : [];
        actions.forEach((action) => {
          const name = typeof action === 'string' ? action : action?.action;
          if (name !== 'submitted_task') return;
          const at = action?.acted_at ? new Date(action.acted_at).getTime() : 0;
          if (!matchId || at >= matchAt) {
            matchId = roleEntry.user_id || roleEntry.user?._id || null;
            matchAt = Number.isNaN(at) ? 0 : at;
          }
        });
      });
      if (matchId) return String(matchId);
      if (!this.isAwaitingSubmission() && this.preparerUsers.length === 1) {
        return String(this.preparerUsers[0].id);
      }
      return null;
    },

    isPreparerSubmitter(userId) {
      const submitterId = this.submitterId();
      return !!submitterId && String(submitterId) === String(userId);
    },

    showPreparerCompleteBadge(userId) {
      return !this.isAwaitingSubmission() && this.isPreparerSubmitter(userId);
    },

    getPreparerAvatarClass(userId) {
      if (this.isAwaitingSubmission()) return 'summary-assignment__avatar--muted';
      if (this.isPreparerSubmitter(userId)) return 'summary-assignment__avatar--completed';
      return 'summary-assignment__avatar--pending';
    },

    isL1UserCompleted(userId) {
      return this.isGroupUserCompleted(userId, this.approverCellModel.l1);
    },

    showL1CompleteBadge(userId) {
      if (this.isRejectedAfterAutoApproval || this.approverCellModel.l1AutoApproved) return false;
      return this.isL1UserCompleted(userId);
    },

    getL1AvatarClass(userId) {
      if (this.isRejectedAfterAutoApproval || this.approverCellModel.l1AutoApproved) {
        return 'summary-assignment__avatar--muted';
      }
      if (!this.approverCellModel.l1?.completed) {
        return 'summary-assignment__avatar--muted';
      }
      if (this.isL1UserCompleted(userId)) {
        return 'summary-assignment__avatar--completed';
      }
      return 'summary-assignment__avatar--pending';
    },

    isL2UserCompleted(userId) {
      return this.isGroupUserCompleted(userId, this.approverCellModel.l2);
    },

    showL2CompleteBadge(userId) {
      if (this.isRejectedAfterAutoApproval) return false;
      return this.isL2UserCompleted(userId);
    },

    getL2AvatarClass(userId) {
      if (this.isRejectedAfterAutoApproval || !this.approverCellModel.l2?.completed) {
        return 'summary-assignment__avatar--muted';
      }
      if (this.isL2UserCompleted(userId)) {
        return 'summary-assignment__avatar--completed';
      }
      return 'summary-assignment__avatar--pending';
    },
  },
};
</script>

